import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { fmtDateTime, fmtDuration } from '../lib/format';
import { BottomNav } from '../components/BottomNav';
import { Pagination } from '../components/Pagination';

const PAGE_SIZE = 15;

interface DutyLog {
  id: string;
  locationName: string;
  startAt: string;
  endAt: string | null;
  durationMinutes: number | null;
  status: 'in_progress' | 'completed' | 'cancelled';
  dutyType: { name: string };
}

const STATUS_META: Record<DutyLog['status'], { label: string; cls: string }> = {
  in_progress: { label: 'กำลังดำเนินการ', cls: 'bg-amber-100 text-amber-700' },
  completed: { label: 'เสร็จสิ้น', cls: 'bg-green-100 text-green-700' },
  cancelled: { label: 'ยกเลิก', cls: 'bg-gray-100 text-gray-600' },
};

export function HistoryPage() {
  const [items, setItems] = useState<DutyLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | DutyLog['status']>('all');

  async function load() {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, pageSize: PAGE_SIZE };
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await api.get<{ data: DutyLog[]; total: number }>('/app/duty-logs', {
        params,
      });
      setItems(data.data);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [statusFilter, page]);

  function changeStatus(s: typeof statusFilter) {
    setStatusFilter(s);
    setPage(1);
  }

  return (
    <div className="mx-auto min-h-screen max-w-md bg-gray-50 pb-24">
      <header className="border-b border-gray-200 bg-white p-4">
        <h1 className="text-lg font-bold">ประวัติการปฏิบัติงาน</h1>
      </header>

      <div className="flex gap-2 overflow-x-auto px-4 pb-2 pt-3">
        {(['all', 'in_progress', 'completed', 'cancelled'] as const).map((s) => (
          <button
            key={s}
            onClick={() => changeStatus(s)}
            className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ${
              statusFilter === s
                ? 'bg-line text-white'
                : 'bg-white text-gray-600 ring-1 ring-gray-200'
            }`}
          >
            {s === 'all' ? 'ทั้งหมด' : STATUS_META[s].label}
          </button>
        ))}
      </div>

      <main className="space-y-3 p-4">
        {loading ? (
          <p className="text-center text-sm text-gray-500">กำลังโหลด...</p>
        ) : items.length === 0 ? (
          <p className="text-center text-sm text-gray-500">ยังไม่มีบันทึก</p>
        ) : (
          items.map((it) => (
            <Link
              key={it.id}
              to={`/duty/${it.id}`}
              className="card block active:bg-gray-50"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{it.locationName}</p>
                  <p className="text-xs text-gray-500">{it.dutyType.name}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    {fmtDateTime(it.startAt)} • {fmtDuration(it.durationMinutes)}
                  </p>
                </div>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${STATUS_META[it.status].cls}`}
                >
                  {STATUS_META[it.status].label}
                </span>
              </div>
            </Link>
          ))
        )}

        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onChange={setPage}
        />
      </main>

      <BottomNav />
    </div>
  );
}
